import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Calendar, Users, BookOpen, Trophy, Film, Loader2,
  BarChart3, UserCheck, Newspaper, CreditCard, Tv
} from "lucide-react";
import { Button } from "../ui/button";

const Dashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiStatus, setApiStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [standings, setStandings] = useState([]);
  const [newsStatus, setNewsStatus] = useState(null);
  const [refreshingNews, setRefreshingNews] = useState(false);
  const [scoresStatus, setScoresStatus] = useState(null);
  const [scrapingScores, setScrapingScores] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [togglingPayment, setTogglingPayment] = useState(false);
  const [togglingTV, setTogglingTV] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchApiStatus();
    fetchStandings();
    fetchNewsStatus();
    fetchScoresStatus();
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/site-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSiteSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch site settings");
    }
  };

  const togglePayment = async () => {
    setTogglingPayment(true);
    try {
      const response = await axios.post(`${API}/admin/site-settings/toggle-payment`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchSiteSettings();
    } catch (error) {
      toast.error("Failed to toggle payment setting");
    } finally {
      setTogglingPayment(false);
    }
  };

  const toggleTV = async () => {
    setTogglingTV(true);
    try {
      const response = await axios.post(`${API}/admin/site-settings/toggle-tv`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchSiteSettings();
    } catch (error) {
      toast.error("Failed to toggle TV section");
    } finally {
      setTogglingTV(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats");
    } finally {
      setLoading(false);
    }
  };

  const fetchApiStatus = async () => {
    try {
      const response = await axios.get(`${API}/football-api/status`);
      setApiStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch API status");
    }
  };

  const fetchStandings = async () => {
    try {
      const response = await axios.get(`${API}/football-api/standings`);
      setStandings(response.data.standings || []);
    } catch (error) {
      console.error("Failed to fetch standings");
    }
  };

  const fetchNewsStatus = async () => {
    try {
      const response = await axios.get(`${API}/news/status`);
      setNewsStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch news status");
    }
  };

  const fetchScoresStatus = async () => {
    try {
      const response = await axios.get(`${API}/scores/status`);
      setScoresStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch scores status");
    }
  };

  const refreshNews = async () => {
    setRefreshingNews(true);
    try {
      const response = await axios.get(`${API}/news/refresh`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || "News refreshed successfully");
      fetchNewsStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to refresh news");
    } finally {
      setRefreshingNews(false);
    }
  };

  const scrapeScores = async () => {
    setScrapingScores(true);
    try {
      const response = await axios.get(`${API}/scores/scrape`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || "Scores scraped successfully");
      fetchScoresStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to scrape scores");
    } finally {
      setScrapingScores(false);
    }
  };

  const toggleScoresScheduler = async (action) => {
    try {
      const response = await axios.get(`${API}/scores/scheduler/${action}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchScoresStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} scores scheduler`);
    }
  };

  const syncFixtures = async () => {
    setSyncing(true);
    try {
      const response = await axios.get(`${API}/football-api/sync-fixtures`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Synced ${response.data.added} new fixtures, updated ${response.data.updated}`);
      fetchStats();
      fetchApiStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync fixtures");
    } finally {
      setSyncing(false);
    }
  };

  const toggleScheduler = async (action) => {
    setSyncing(true);
    try {
      const response = await axios.get(`${API}/football-api/scheduler/${action}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchApiStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} scheduler`);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="spinner"></div></div>;
  }

  const statCards = [
    { label: "Total Users", value: stats?.users || 0, icon: Users, color: "bg-blue-500" },
    { label: "Subscribers", value: stats?.subscribers || 0, icon: UserCheck, color: "bg-green-500" },
    { label: "Fixtures", value: stats?.fixtures || 0, icon: Calendar, color: "bg-[#7C2C3B]" },
    { label: "Players", value: stats?.players || 0, icon: Users, color: "bg-purple-500" },
    { label: "History Years", value: stats?.history_years || 0, icon: BookOpen, color: "bg-orange-500" },
    { label: "Cup Records", value: stats?.cups || 0, icon: Trophy, color: "bg-yellow-500" },
    { label: "Entertainment", value: stats?.entertainment || 0, icon: Film, color: "bg-pink-500" },
    { label: "News Articles", value: (newsStatus?.article_counts?.men || 0) + (newsStatus?.article_counts?.women || 0), icon: Newspaper, color: "bg-cyan-500" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-3xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Barlow Condensed' }}>DASHBOARD</h1>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {statCards.map(stat => (
          <div key={stat.label} className="bg-white rounded-xl p-6 border border-slate-200">
            <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center mb-4`}>
              <stat.icon className="w-6 h-6 text-white" />
            </div>
            <div className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>{stat.value}</div>
            <div className="text-sm text-slate-500">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Payment Settings Card */}
      <div className={`rounded-xl p-6 mb-8 border-2 ${siteSettings?.payment_enabled ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-300'}`} data-testid="payment-settings-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${siteSettings?.payment_enabled ? 'bg-green-500' : 'bg-amber-500'}`}>
              <CreditCard className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
                PAYMENT REQUIREMENT
              </h2>
              <p className={`text-sm ${siteSettings?.payment_enabled ? 'text-green-700' : 'text-amber-700'}`}>
                {siteSettings?.payment_enabled 
                  ? "Payment is ENABLED - Users must pay £2.50/month to subscribe"
                  : "Payment is DISABLED - Users can subscribe for FREE (no Stripe checkout)"
                }
              </p>
              {siteSettings?.updated_by && (
                <p className="text-xs text-slate-500 mt-1">
                  Last changed by {siteSettings.updated_by} on {new Date(siteSettings.updated_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className={`px-4 py-2 rounded-full text-sm font-bold ${siteSettings?.payment_enabled ? 'bg-green-500 text-white' : 'bg-amber-500 text-white'}`}>
              {siteSettings?.payment_enabled ? "💳 PAYMENTS ON" : "🆓 FREE MODE"}
            </div>
            <Button
              onClick={togglePayment}
              disabled={togglingPayment}
              variant={siteSettings?.payment_enabled ? "destructive" : "default"}
              className={siteSettings?.payment_enabled ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}
              data-testid="toggle-payment-btn"
            >
              {togglingPayment ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : siteSettings?.payment_enabled ? (
                "Turn OFF Payments"
              ) : (
                "Turn ON Payments"
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* TV Section Settings Card */}
      <div className={`rounded-xl p-6 mb-8 border-2 ${siteSettings?.tv_section_enabled ? 'bg-blue-50 border-blue-300' : 'bg-slate-50 border-slate-300'}`} data-testid="tv-settings-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${siteSettings?.tv_section_enabled ? 'bg-blue-500' : 'bg-slate-400'}`}>
              <Tv className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
                ALL WHUFC GAMES TV
              </h2>
              <p className={`text-sm ${siteSettings?.tv_section_enabled ? 'text-blue-700' : 'text-slate-600'}`}>
                {siteSettings?.tv_section_enabled 
                  ? "TV Section is VISIBLE on the homepage"
                  : "TV Section is HIDDEN from the homepage"
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className={`px-4 py-2 rounded-full text-sm font-bold ${siteSettings?.tv_section_enabled ? 'bg-blue-500 text-white' : 'bg-slate-400 text-white'}`}>
              {siteSettings?.tv_section_enabled ? "📺 VISIBLE" : "🚫 HIDDEN"}
            </div>
            <Button
              onClick={toggleTV}
              disabled={togglingTV}
              variant={siteSettings?.tv_section_enabled ? "destructive" : "default"}
              className={siteSettings?.tv_section_enabled ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}
              data-testid="toggle-tv-btn"
            >
              {togglingTV ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : siteSettings?.tv_section_enabled ? (
                "Hide TV Section"
              ) : (
                "Show TV Section"
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Football API Section */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* API Sync Panel */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h2 className="text-xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            <BarChart3 className="w-5 h-5 inline mr-2 text-[#2BAEE5]" />
            FOOTBALL API
          </h2>
          
          <div className={`p-4 rounded-lg mb-4 ${apiStatus?.configured ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
            <p className={`text-sm ${apiStatus?.configured ? 'text-green-700' : 'text-yellow-700'}`}>
              {apiStatus?.message || "Checking API status..."}
            </p>
          </div>

          {apiStatus?.configured ? (
            <div className="space-y-4">
              {/* Scheduler Status */}
              <div className={`p-3 rounded-lg ${apiStatus?.scheduler_running ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 border border-slate-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${apiStatus?.scheduler_running ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`}></div>
                      <span className="text-sm font-medium text-slate-700">
                        Auto-Sync: {apiStatus?.scheduler_running ? 'Running' : 'Stopped'}
                      </span>
                    </div>
                    {apiStatus?.last_sync && (
                      <p className="text-xs text-slate-500 mt-1">
                        Last sync: {new Date(apiStatus.last_sync).toLocaleString()}
                      </p>
                    )}
                    {apiStatus?.last_sync_result?.live_matches > 0 && (
                      <p className="text-xs text-green-600 mt-1">
                        🔴 {apiStatus.last_sync_result.live_matches} live match(es)
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant={apiStatus?.scheduler_running ? "destructive" : "default"}
                    onClick={() => toggleScheduler(apiStatus?.scheduler_running ? 'stop' : 'start')}
                    disabled={syncing}
                    className={apiStatus?.scheduler_running ? '' : 'bg-green-600 hover:bg-green-700'}
                    data-testid="toggle-scheduler-btn"
                  >
                    {apiStatus?.scheduler_running ? 'Stop' : 'Start'}
                  </Button>
                </div>
              </div>

              {/* Manual Sync Buttons */}
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  onClick={syncFixtures}
                  disabled={syncing}
                  className="bg-[#2BAEE5] hover:bg-[#228BB7]"
                  data-testid="sync-fixtures-btn"
                >
                  {syncing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> SYNCING...</>
                  ) : (
                    <><Calendar className="w-4 h-4 mr-2" /> SYNC FIXTURES</>
                  )}
                </Button>
                <Button 
                  onClick={syncFixtures}
                  disabled={syncing}
                  className="bg-[#7C2C3B] hover:bg-[#63232F]"
                  data-testid="sync-live-scores-btn"
                >
                  {syncing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> SYNCING...</>
                  ) : (
                    <><BarChart3 className="w-4 h-4 mr-2" /> SYNC LIVE SCORES</>
                  )}
                </Button>
              </div>

              <p className="text-xs text-slate-500 text-center mt-2">
                Auto-sync runs every 15 minutes when enabled
              </p>
            </div>
          ) : (
            <div className="text-sm text-slate-600">
              <p className="mb-2">To enable live data sync:</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>Get an API key from <a href="https://www.api-football.com" target="_blank" className="text-[#2BAEE5] underline">api-football.com</a></li>
                <li>Add it to <code className="bg-slate-100 px-1 rounded">/app/backend/.env</code></li>
                <li>Set <code className="bg-slate-100 px-1 rounded">API_FOOTBALL_KEY=your_key</code></li>
                <li>Restart the backend server</li>
              </ol>
              
              {/* Sync buttons - disabled when API not configured */}
              <div className="grid grid-cols-2 gap-2 mt-4">
                <Button 
                  onClick={syncFixtures}
                  disabled={true}
                  className="bg-slate-300 cursor-not-allowed"
                  data-testid="sync-fixtures-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" /> SYNC FIXTURES
                </Button>
                <Button 
                  onClick={syncFixtures}
                  disabled={true}
                  className="bg-slate-300 cursor-not-allowed"
                  data-testid="sync-live-scores-btn"
                >
                  <BarChart3 className="w-4 h-4 mr-2" /> SYNC LIVE SCORES
                </Button>
              </div>
              <p className="text-xs text-slate-400 text-center mt-2">
                Configure API key to enable sync buttons
              </p>
            </div>
          )}
        </div>

        {/* Men's League Standings (currently EFL Championship) */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h2 className="text-xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            <Trophy className="w-5 h-5 inline mr-2 text-[#F8D742]" />
            EFL CHAMPIONSHIP TABLE
          </h2>
          
          {standings.length > 0 ? (
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left text-xs text-slate-500 uppercase">
                    <th className="pb-2">#</th>
                    <th className="pb-2">Team</th>
                    <th className="pb-2 text-center">P</th>
                    <th className="pb-2 text-center">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.slice(0, 10).map(team => (
                    <tr key={team.position} className={team.is_west_ham ? "bg-[#7C2C3B]/10 font-semibold" : ""}>
                      <td className="py-1">{team.position}</td>
                      <td className="py-1 truncate max-w-24">{team.team}</td>
                      <td className="py-1 text-center">{team.played}</td>
                      <td className="py-1 text-center font-bold">{team.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              {apiStatus?.configured 
                ? "No standings data available yet" 
                : "Configure API key to view standings"}
            </p>
          )}
        </div>
      </div>

      {/* News Scraper Section */}
      <div className="mt-6 bg-white rounded-xl p-6 border border-slate-200">
        <h2 className="text-xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
          <Newspaper className="w-5 h-5 inline mr-2 text-[#2BAEE5]" />
          NEWS SCRAPER
        </h2>
        
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-[#0F172A]">{newsStatus?.article_counts?.men || 0}</div>
            <div className="text-sm text-slate-500">Men's Articles</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-[#0F172A]">{newsStatus?.article_counts?.women || 0}</div>
            <div className="text-sm text-slate-500">Women's Articles</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-[#0F172A]">{newsStatus?.article_counts?.latest || 0}</div>
            <div className="text-sm text-slate-500">Latest (24h)</div>
          </div>
        </div>

        <div className={`p-3 rounded-lg mb-4 ${newsStatus?.scheduler_running ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${newsStatus?.scheduler_running ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`}></div>
              <span className="text-sm font-medium text-slate-700">
                {newsStatus?.scheduler_running ? 'Auto-sync active' : 'Auto-sync inactive'}
              </span>
            </div>
            {newsStatus?.last_sync && (
              <span className="text-xs text-slate-500">
                Last sync: {new Date(newsStatus.last_sync).toLocaleString()}
              </span>
            )}
          </div>
        </div>

        <Button 
          onClick={refreshNews}
          disabled={refreshingNews}
          className="w-full bg-[#2BAEE5] hover:bg-[#228BB7]"
        >
          {refreshingNews ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> REFRESHING NEWS...</>
          ) : (
            <><Newspaper className="w-4 h-4 mr-2" /> REFRESH NEWS NOW</>
          )}
        </Button>
        
        <p className="text-xs text-slate-500 text-center mt-2">
          News automatically refreshes every 30 minutes from BBC, Sky Sports, Guardian, and more
        </p>
      </div>

      {/* Live Scores Scraper Section */}
      <div className="mt-6 bg-white rounded-xl p-6 border border-slate-200">
        <h2 className="text-xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
          <BarChart3 className="w-5 h-5 inline mr-2 text-[#7C2C3B]" />
          LIVE SCORES SCRAPER
        </h2>
        
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-[#0F172A]">{scoresStatus?.matches_today || 0}</div>
            <div className="text-sm text-slate-500">Pending Matches Today</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-[#0F172A]">{scoresStatus?.last_result?.fixtures_updated || 0}</div>
            <div className="text-sm text-slate-500">Last Update Count</div>
          </div>
        </div>

        <div className={`p-3 rounded-lg mb-4 ${scoresStatus?.scheduler_running ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${scoresStatus?.scheduler_running ? 'bg-green-500 animate-pulse' : 'bg-amber-400'}`}></div>
              <span className="text-sm font-medium text-slate-700">
                {scoresStatus?.scheduler_running ? 'Auto-sync every 5 mins' : 'Auto-sync inactive'}
              </span>
            </div>
            {scoresStatus?.last_sync && (
              <span className="text-xs text-slate-500">
                Last: {new Date(scoresStatus.last_sync).toLocaleString()}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          <Button 
            onClick={scrapeScores}
            disabled={scrapingScores}
            className="bg-[#7C2C3B] hover:bg-[#63232F]"
          >
            {scrapingScores ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> SCRAPING...</>
            ) : (
              <><BarChart3 className="w-4 h-4 mr-2" /> SCRAPE SCORES NOW</>
            )}
          </Button>
          
          <Button 
            onClick={() => toggleScoresScheduler(scoresStatus?.scheduler_running ? 'stop' : 'start')}
            variant={scoresStatus?.scheduler_running ? "outline" : "default"}
            className={!scoresStatus?.scheduler_running ? "bg-green-600 hover:bg-green-700" : ""}
          >
            {scoresStatus?.scheduler_running ? 'Stop Auto-Sync' : 'Start Auto-Sync'}
          </Button>
        </div>
        
        <div className="text-xs text-slate-500 space-y-1">
          <p className="font-medium">Sources: BBC Sport, Sky Sports, ESPN, FlashScore, Google</p>
          <p>Scrapes live scores automatically every 5 minutes when enabled. Click "Scrape Scores Now" to update immediately.</p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
